import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useTranslation } from "react-i18next";
import { Flame } from "lucide-react";

const THOUGHTS = {
  generic: {
    fr: ["Je remonte le fil…", "Je cherche l'angle que personne ne regarde…", "Je relie les idées…", "Je choisis les mots justes…"],
    en: ["Pulling at the thread…", "Looking for the angle no one sees…", "Connecting the ideas…", "Choosing the right words…"],
    es: ["Sigo el hilo…", "Busco el ángulo que nadie ve…", "Conecto las ideas…", "Elijo las palabras justas…"],
    nl: ["Ik volg de draad…", "Ik zoek de hoek die niemand ziet…", "Ik verbind de ideeën…", "Ik kies de juiste woorden…"],
  },
  proces: {
    fr: ["Le Procureur prépare son réquisitoire…", "L'Avocat rassemble ses arguments…", "Le Juge pèse le pour et le contre…", "Le verdict s'écrit…"],
    en: ["The Prosecutor drafts the charge…", "The Advocate gathers arguments…", "The Judge weighs both sides…", "The verdict is being written…"],
    es: ["El Fiscal prepara su acusación…", "El Abogado reúne sus argumentos…", "El Juez sopesa ambos lados…", "Se escribe el veredicto…"],
    nl: ["De Aanklager stelt de aanklacht op…", "De Advocaat verzamelt argumenten…", "De Rechter weegt beide kanten…", "Het oordeel wordt geschreven…"],
  },
  doute: {
    fr: ["J'aiguise ma contradiction…", "Je cherche la faille…", "Je prépare le contre-pied…"],
    en: ["Sharpening my rebuttal…", "Looking for the flaw…", "Preparing the counter…"],
    es: ["Afilo mi contradicción…", "Busco la grieta…", "Preparo la réplica…"],
    nl: ["Ik scherp mijn tegenspraak…", "Ik zoek de zwakke plek…", "Ik bereid het weerwoord voor…"],
  },
  verites: {
    fr: ["Je remonte aux premières railleries…", "Je retrace le combat…", "Je regarde l'idée devenir évidence…"],
    en: ["Back to the first mockery…", "Tracing the fight…", "Watching the idea become obvious…"],
    es: ["Vuelvo a las primeras burlas…", "Rastreo el combate…", "Veo la idea volverse evidente…"],
    nl: ["Terug naar de eerste spot…", "Ik traceer de strijd…", "Ik zie het idee vanzelfsprekend worden…"],
  },
  debate: {
    fr: ["L'Ardent s'enflamme…", "Le Nuancé tempère…", "Les deux voies se répondent…"],
    en: ["The Ardent flares up…", "The Nuanced tempers…", "The two voices answer each other…"],
    es: ["El Ardiente se enciende…", "El Matizado modera…", "Las dos voces se responden…"],
    nl: ["De Vurige laait op…", "De Genuanceerde tempert…", "De twee stemmen antwoorden elkaar…"],
  },
  expedition: {
    fr: ["Je pars en reconnaissance…", "Je fouille les meilleures pistes…", "Je choisis ce qui vaut le détour…", "Je rassemble ton expédition…"],
    en: ["Scouting ahead…", "Digging through the best leads…", "Picking what's worth the detour…", "Gathering your expedition…"],
    es: ["Salgo a explorar…", "Rastreo las mejores pistas…", "Elijo lo que vale el desvío…", "Reúno tu expedición…"],
    nl: ["Ik ga op verkenning…", "Ik doorzoek de beste sporen…", "Ik kies wat de omweg waard is…", "Ik stel je expeditie samen…"],
  },
};

export function AiEmber({ size = 44 }) {
  return (
    <span className="relative inline-flex items-center justify-center" style={{ width: size, height: size }}>
      <span className="ai-ember-halo absolute inset-0 rounded-full" style={{ background: "radial-gradient(circle, hsl(32 90% 55% / 0.55), transparent 70%)", filter: "blur(6px)" }} />
      <span className="ai-ember-core relative flex items-center justify-center rounded-full" style={{ width: size * 0.62, height: size * 0.62, background: "radial-gradient(circle at 50% 35%, #ffd9a0, #e08a2b 55%, #b4571a)" }}>
        <Flame size={Math.round(size * 0.32)} className="text-[#3a1e05]" strokeWidth={2} />
      </span>
    </span>
  );
}

export function Shimmer({ variant = "lines" }) {
  if (variant === "cards") {
    return (
      <div className="grid gap-3 sm:grid-cols-3 w-full mt-3" aria-hidden>
        {[0, 1, 2].map((i) => (
          <div key={i} className="rounded-2xl border border-white/10 p-4 space-y-2.5">
            <div className="ai-skel h-8 w-8 rounded-xl" />
            <div className="ai-skel h-3 w-2/3" />
            <div className="ai-skel h-3 w-full" />
            <div className="ai-skel h-3 w-5/6" />
          </div>
        ))}
      </div>
    );
  }
  return (
    <div className="w-full max-w-lg mx-auto space-y-2.5 mt-3" aria-hidden>
      <div className="ai-skel h-3 w-3/4 mx-auto" />
      <div className="ai-skel h-3 w-full" />
      <div className="ai-skel h-3 w-5/6 mx-auto" />
    </div>
  );
}

export function AiThinking({ variant = "generic", skeleton = null, testId = "ai-thinking" }) {
  const { i18n } = useTranslation();
  const lng = (i18n.language || "fr").slice(0, 2);
  const bank = THOUGHTS[variant] || THOUGHTS.generic;
  const set = bank[lng] || bank.fr;
  const [i, setI] = useState(0);
  useEffect(() => {
    const t = setInterval(() => setI((v) => (v + 1) % set.length), 2200);
    return () => clearInterval(t);
  }, [set.length]);
  return (
    <div className="flex flex-col items-center justify-center gap-4 py-10 w-full" data-testid={testId}>
      <AiEmber />
      <AnimatePresence mode="wait">
        <motion.p key={i} initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -6 }}
          transition={{ duration: 0.4 }} className="font-display italic text-foreground/60 text-center text-base px-6">
          {set[i]}
        </motion.p>
      </AnimatePresence>
      {skeleton && <Shimmer variant={skeleton} />}
    </div>
  );
}
